import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import ComicCard from '@/components/base/ComicCard';
import { categories } from '@/mocks/categories';
import { allComics } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'latest' | 'hot' | 'rating'>('latest');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 12;

  const activeCategory = categories.find((c) => c.slug === slug) || categories[0];

  const filteredComics: Comic[] = useMemo(() => {
    let result: Comic[];
    if (slug === 'all') {
      result = allComics;
    } else if (slug === 'lianzai') {
      result = allComics.filter((c) => c.status === '连载中');
    } else if (slug === 'wanjie') {
      result = allComics.filter((c) => c.status === '已完结');
    } else {
      const catName = activeCategory.name;
      result = allComics.filter((c) => c.category === catName);
    }

    switch (sortBy) {
      case 'hot':
        return [...result].sort((a, b) => parseFloat(b.views.replace('万', '')) - parseFloat(a.views.replace('万', '')));
      case 'rating':
        return [...result].sort((a, b) => b.rating - a.rating);
      default:
        return [...result].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    }
  }, [slug, sortBy]);

  const totalPages = Math.ceil(filteredComics.length / pageSize);
  const pagedComics = filteredComics.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://xiuxiumanhua.com/' },
      { '@type': 'ListItem', position: 2, name: activeCategory.name, item: `https://xiuxiumanhua.com/category/${slug}` },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${activeCategory.name} - 羞羞漫画分类浏览`}
        description={`羞羞漫画${activeCategory.name}专区，${activeCategory.description}。海量正版${activeCategory.name}漫画在线免费阅读，每日更新。`}
        keywords={`${activeCategory.name}漫画,羞羞漫画,${activeCategory.name}漫画在线阅读,正版漫画`}
        canonicalUrl={`/category/${slug}`}
        schemaData={breadcrumbSchema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-16">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-10 md:py-14">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-1.5 text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-950 font-medium">{activeCategory.name}</span>
            </nav>

            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
              {activeCategory.name}
            </h1>
            <p className="text-sm text-foreground-500 mb-8">{activeCategory.description}</p>

            {/* Category Tabs */}
            <div className="flex flex-wrap gap-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Sort Bar */}
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <p className="text-sm text-foreground-500">
                共找到 <strong className="text-foreground-950">{filteredComics.length}</strong> 部作品
              </p>
              <div className="flex items-center gap-1 bg-background-100 rounded-full px-1 py-1">
                {[
                  { key: 'latest', label: '最新' },
                  { key: 'hot', label: '最热' },
                  { key: 'rating', label: '评分' },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => { setSortBy(opt.key as typeof sortBy); setCurrentPage(1); }}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      sortBy === opt.key ? 'bg-background-50 text-foreground-950' : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Comic Grid */}
            {pagedComics.length > 0 ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 md:gap-5" data-product-shop>
                  {pagedComics.map((comic) => (
                    <ComicCard key={comic.id} comic={comic} />
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-10">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="w-9 h-9 rounded-full flex items-center justify-center bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                      <button
                        key={p}
                        onClick={() => setCurrentPage(p)}
                        className={`w-9 h-9 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                          p === currentPage
                            ? 'bg-primary-500 text-background-50'
                            : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="w-9 h-9 rounded-full flex items-center justify-center bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-book-open-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无漫画作品</p>
                <Link
                  to="/category/all"
                  className="inline-flex items-center gap-1 mt-4 text-sm text-primary-500 hover:text-primary-600 cursor-pointer whitespace-nowrap"
                >
                  浏览全部漫画 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            )}
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}